import Vue from 'vue';
import Vuex from 'vuex';
import accountApi from '@/plugins/api/account';
import walletApi from '@/plugins/api/wallet';
import regionSwitcher from './region-switcher';
import partnerKey from './partnerkey';

Vue.use(Vuex);

export default new Vuex.Store({
	state: {
		isLoggedIn: null, // init value for representing it's not initialized

		nexonTag: '',
		emailVerified: false,

		balance: 0,
		currency: '',
		balanceLoading: false,

		region: environment.region,
		ipMembershipNo: 0,
		isVietnam: false,
		isBlocked: false,
		country: '',
		games: {
			all: [],
			pc: [],
			xbox: [],
			psn: [],
			mac: [],
			ios: [],
			android: [],
		},
		footers: [],
		deviceId: '',

		isReconsentUser: false,

		isMac: /Macintosh|MacIntel|MacPPC/i.test(navigator.userAgent),
	},
	getters: {
		cmsRegionCode: (state) => {
			if (state.region === 'sea' && state.isVietnam) {
				return 'vietnam';
			}
			return state.region;
		},
		isJapan: (state) => {
			return state.region === 'japan';
		},
	},
	actions: {
		async checkLogin({ commit, dispatch, state }) {
			if (!(navigator.userAgent.indexOf('NexonLauncher') > 0)) {
				// call the status API only for the web since the GNB is not displayed in the launcher
				const deviceId = localStorage.getItem('device_id') ? localStorage.getItem('device_id') : '';
				commit('setDeviceId', deviceId);

				return this._vm.$regionalAPI
					.get(`/no-auth/web/login/status?_t=${Date.now()}`, {
						params: {
							...(state.deviceId ? { device_id: state.deviceId } : {}),
						},
					})
					.then((response) => {
						commit('setLoggedIn', true);
						dispatch('getAccountData');

						const loginSessionExpiresIn = response.data.loginSessionExpiresIn;
						if (!localStorage.getItem('refreshSessionAt'))
							localStorage.setItem('refreshSessionAt', Date.now() + (loginSessionExpiresIn - 3600) * 1000); //refresh session when at least one hour remain before session will expire

						dispatch('refreshLoginTokenLoop');
					})
					.catch((error) => {
						const { status, data } = error.response || {};
						if (status === 403 && data.message === 'RE_CONSENT_REQUIRED') {
							commit('setIsReconsentUser', true);
						}
						commit('setLoggedIn', false);
						if (localStorage.getItem('refreshSessionAt')) localStorage.removeItem('refreshSessionAt'); // remove the refreshSessionAt if not logged in order to add a new refreshSessionAt after login
						if (localStorage.getItem('refreshCounter')) localStorage.removeItem('refreshCounter'); // remove the refreshCounter if not logged in for resetting it to 0 on calling refreshLoginToken first time after the login
					});
			}
		},
		async getAccountData({ commit }) {
			try {
				const { data } = await accountApi.get('/account');
				commit('setAccountData', data);
			} catch {
				if (location.href.indexOf('add-balance') >= 0) location.href = `${location.protocol}//${location.host}/account/logout`;
			}
		},
		async getBalances({ commit, getters }) {
			if (getters.isJapan) return;
			commit('setBalanceLoading', true);
			const { data } = await walletApi.get(`/balance`);
			commit('setBalances', data);
			commit('setBalanceLoading', false);
		},
		async checkRegionInfo({ commit }) {
			const { data } = await this._vm.$countryAPI.get('/no-auth/membership-no');
			commit('setRegionInfo', data);
		},
		async getGames({ commit, getters }) {
			if (getters.isJapan) return;
			const { data } = await this._vm.$cdnAPI.get(`frontend/v3/${getters.cmsRegionCode}/gamelist.json`);
			commit('setGames', data);
		},
		async getFooters({ commit, getters }) {
			if (getters.isJapan) return;
			const { data } = await this._vm.$cdnAPI.get(`web/v2/${getters.cmsRegionCode}/gnb/footer.json`);
			commit('setFooters', data);
		},
		async refreshLoginToken({ state }) {
			let refreshCounter = parseInt(localStorage.getItem('refreshCounter') || '0', 10);

			return await this._vm.$regionalAPI
				.put('/web/login/refresh', {
					...(state.deviceId ? { deviceId: state.deviceId } : {}),
					refreshCounter: refreshCounter, // refreshCounter on the first API call should be 0 and increment the value on the next API call
				})
				.then((response) => {
					// set new refreshSessionAt (as one hour before session will expire) and increment the refreshCounter
					const loginSessionExpiresIn = response.data.loginSessionExpiresIn;
					localStorage.setItem('refreshSessionAt', Date.now() + (loginSessionExpiresIn - 3600) * 1000);
					localStorage.setItem('refreshCounter', refreshCounter + 1);
				});
		},
		async refreshLoginTokenLoop({ dispatch }) {
			for (;;) {
				const refreshAt = parseInt(localStorage.getItem('refreshSessionAt') || '0', 10);
				const currentTime = Date.now();
				if (currentTime > refreshAt) {
					await dispatch('refreshLoginToken');
				}
				await new Promise((resolve) => setTimeout(resolve, 60 * 1000)); // wait 1 minute before checking again
			}
		},
	},
	mutations: {
		setLoggedIn(state, payload) {
			state.isLoggedIn = payload;
		},
		setAccountData(state, payload) {
			state.nexonTag = payload.nexonTag;
			state.emailVerified = payload.isVerified;
		},
		setBalanceLoading(state, payload) {
			state.balanceLoading = payload;
		},
		setBalances(state, payload) {
			state.balance = payload.totalAmount;
			state.currency = payload.currency;
		},
		setRegionInfo(state, value) {
			state.ipMembershipNo = value.membershipNo;
			state.isVietnam = value.isVietnam;
			state.isBlocked = value.isBlocked;
			state.country = value.country;
		},
		setGames(state, value) {
			const games = value.filter((x) => x.showInAllGamesPage);
			state.games.all = games;
			state.games.pc = games.filter((x) => x.pc || x.steam);
			state.games.xbox = games.filter((x) => x.xbox);
			state.games.psn = games.filter((x) => x.psn);
			state.games.mac = games.filter((x) => x.pcMac);
			state.games.ios = games.filter((x) => x.ios);
			state.games.android = games.filter((x) => x.android);
		},
		setFooters(state, value) {
			state.footers = value;
		},
		setDeviceId(state, value) {
			state.deviceId = value;
		},
		setIsReconsentUser(state, value) {
			state.isReconsentUser = value;
		},
	},
	modules: {
		regionSwitcher,
		partnerKey,
	},
});
